import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import SvgMapper from "../../features/common/SvgMapper";
import { getStudentMarks } from "../../../application/services/studentDashboard";

const PAGE_SIZE = 10;

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

const getScoreColor = (percent) => {
  const p = parseFloat(percent) || 0;
  if (p >= 70) return "green";
  if (p >= 40) return "yellow";
  return "gray";
};

const mapItem = (item) => ({
  id: item.interview_id,
  title: item.interview_name || item.category || "Interview",
  date: formatDate(item.start_time),
  score: `${item.percent ?? 0}%`,
  color: getScoreColor(item.percent),
});

const RecentPerformance = () => {
  const { user } = useSelector((state) => state.login.student);
  const email = user?.email || "";
  const [recentPerformances, setRecentPerformances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const loadMoreRef = useRef(null);

  const loadPage = useCallback(
    (pageNum, append = false) => {
      if (!email) return;
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      getStudentMarks(email, pageNum, PAGE_SIZE)
        .then((res) => {
          const data = res?.data || [];
          const arr = Array.isArray(data) ? data : [];
          const mapped = arr.map(mapItem);
          setRecentPerformances((prev) => (append ? [...prev, ...mapped] : mapped));
          const total = res?.pagination?.total;
          const loadedCount = append ? (pageNum - 1) * PAGE_SIZE + mapped.length : mapped.length;
          setHasMore(
            typeof total === "number" ? loadedCount < total : mapped.length === PAGE_SIZE
          );
        })
        .catch((err) => {
          console.error("Error fetching recent performance:", err);
          setHasMore(false);
        })
        .finally(() => {
          setLoading(false);
          setLoadingMore(false);
        });
    },
    [email]
  );

  useEffect(() => {
    if (!email) return;
    setPage(1);
    setHasMore(true);
    loadPage(1, false);
  }, [email, loadPage]);

  useEffect(() => {
    if (!loadMoreRef.current || !hasMore || loading || loadingMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting && hasMore && !loading && !loadingMore) {
          const nextPage = page + 1;
          setPage(nextPage);
          loadPage(nextPage, true);
        }
      },
      { rootMargin: "100px", threshold: 0.1 }
    );
    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, page, loadPage]);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Recent Performance
      </h3>
      <div className="space-y-4 max-h-[400px] overflow-y-auto">
        {loading ? (
          <p className="text-sm text-gray-500 italic">Loading...</p>
        ) : recentPerformances.length > 0 ? (
          <>
            {recentPerformances.map((item, index) => (
              <div
                key={item.id || index}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  <div
                    className={`p-2 rounded-lg ${
                      item.color === "green"
                        ? "bg-green-100"
                        : item.color === "yellow"
                        ? "bg-yellow-100"
                        : "bg-gray-100"
                    }`}
                  >
                    {SvgMapper.award(
                      `w-4 h-4 ${
                        item.color === "green"
                          ? "text-green-600"
                          : item.color === "yellow"
                          ? "text-yellow-600"
                          : "text-gray-600"
                      }`
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{item.title}</p>
                    <p className="text-sm text-gray-600">{item.date}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-gray-900">{item.score}</p>
                  <button className="text-blue-600 hover:text-blue-700 text-sm cursor-pointer">
                    View Details
                  </button>
                </div>
              </div>
            ))}
            <div ref={loadMoreRef} className="min-h-4 flex items-center justify-center py-2">
              {loadingMore && <p className="text-sm text-gray-500 italic">Loading more...</p>}
              {!hasMore && recentPerformances.length > 0 && (
                <p className="text-sm text-gray-500 italic">No more records</p>
              )}
            </div>
          </>
        ) : (
          <p className="text-sm text-gray-500 italic">
            No recent performance data
          </p>
        )}
      </div>
    </div>
  );
};

export default RecentPerformance;
